import { PrismDatepicker } from '@prism-ui/react'
import { dayFromDatepicker, isoToDisplayDate, toEndOfDay, toStartOfDay, type DatepickerDetail } from './utils'
import './DatePicker.css'

type Props = {
  value?: string
  defaultValue?: string
  className?: string
  style?: object
  placeholder?: string
  label?: string
  disabled?: boolean
  required?: boolean
  isInvalid?: boolean
  minDate?: string
  maxDate?: string
  onChange?: (selectedDate:string, inputValue:string)=>void
  // Emit the instant at 23:59:59.999 rather than 00:00:00.000. Deadlines are inclusive of
  // the day the admin picks, so a "hard deadline" of the 14th must not expire at midnight.
  endOfDay?: boolean
  // Interpret and emit the calendar day in UTC rather than the browser's zone.
  utc?: boolean
  // Mark this field as edited-but-unsaved. Rendered as a ring on the wrapper rather than a
  // background, because the wrapper sits behind prism-datepicker's own opaque input.
  changed?: boolean
}

/**
 * Thin wrapper over <prism-datepicker> that preserves the ISO-string contract the previous
 * react-bootstrap implementation exposed, so callers did not have to change.
 *
 * The old component rendered its calendar into a react-bootstrap <Overlay>, which forced
 * every caller inside a scrolling table to pass `calendarContainer={() => document.body}`
 * to escape the clipping context. prism-datepicker has no portal and relies on no ancestor
 * establishing one; the AdminPane layout no longer does, so that prop is gone.
 */
const DatePicker = ({
  value, defaultValue, className, style, placeholder, label,
  disabled = false, required, isInvalid, minDate, maxDate,
  onChange, endOfDay = false, utc = false, changed = false,
}:Props) => {
  const display = isoToDisplayDate(value ?? defaultValue, utc)

  const handleChange = (e:CustomEvent<DatepickerDetail>) => {
    if (!onChange) return
    const day = dayFromDatepicker(e.detail)
    if (!day) {
      onChange('', '')
      return
    }
    // Anchor at noon UTC before converting so the calendar day survives a timezone shift
    // in either direction; this mirrors what makeDateValues did previously.
    const anchored = new Date(`${day}T12:00:00.000Z`)
    const iso = endOfDay ? toEndOfDay(anchored, utc) : toStartOfDay(anchored, utc)
    onChange(iso, isoToDisplayDate(iso, utc))
  }

  return (
    <div
      className={[className, changed ? 'date-picker-changed' : ''].filter(Boolean).join(' ') || undefined}
      style={style}
      title={changed ? 'Unsaved change' : undefined}
    >
      <PrismDatepicker
        value={display}
        label={label ?? 'Date'}
        hideLabel={!label}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        invalid={isInvalid}
        min={minDate ? new Date(minDate) : undefined}
        max={maxDate ? new Date(maxDate) : undefined}
        onPrismChange={handleChange}
      />
    </div>
  )
}

export default DatePicker
